import { useState } from "react";
import { useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { useUploadProductPhoto } from "../../lib/queries";
import type { Product } from "../../lib/types";
import { PHOTO_PICK_MAX, preparePhoto } from "./photos";

type Image = Product["images"][number];

/** Photos a product may have uploaded (links from imports do not count). */
export const UPLOADS_MAX = 5;

/** The small version of a photo: uploaded ones have a 400 px thumbnail. */
export const thumbUrl = (img: Image) => (img.id ? `${img.url}?size=thumb` : img.url);

/**
 * A product's photos: upload up to five (prepared in the browser), drag to reorder,
 * the first one is the main one. Uploads are saved at once; order and removals are
 * saved with the rest of the product.
 */
export function PhotoField({
  productId,
  images,
  canEdit,
  onChange,
}: {
  productId: string | null;
  images: Image[];
  canEdit: boolean;
  onChange: (images: Image[]) => void;
}) {
  const upload = useUploadProductPhoto();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState<number | null>(null);
  const [link, setLink] = useState("");
  const uploaded = images.filter((i) => i.id).length;
  const room = UPLOADS_MAX - uploaded;

  const move = (from: number, to: number) => {
    if (to < 0 || to >= images.length || from === to) return;
    const next = [...images];
    const [item] = next.splice(from, 1);
    if (item) next.splice(to, 0, item);
    onChange(next);
  };

  async function onFiles(list: FileList | null) {
    if (!list?.length || !productId) return;
    const files = [...list];
    if (files.length > room) toast(`Solo caben ${room} fotos más: se suben las primeras.`);
    setBusy(true);
    let current = images;
    for (const file of files.slice(0, room)) {
      if (file.size > PHOTO_PICK_MAX) {
        toast(`${file.name} pesa más de 25 MB.`);
        continue;
      }
      const prepared = await preparePhoto(file);
      if (!prepared) {
        toast(`No se pudo leer ${file.name}. Usa una foto JPG, PNG o WebP.`);
        continue;
      }
      try {
        const saved = await upload.mutateAsync({ id: productId, files: prepared });
        const added = saved.images.at(-1);
        if (added) {
          current = [...current, added];
          onChange(current);
        }
      } catch (err) {
        toast(
          err instanceof ApiError && err.code === "conflict"
            ? "Ya tiene 5 fotos subidas."
            : "No se pudo subir la foto.",
        );
        break;
      }
    }
    setBusy(false);
  }

  return (
    <div className="field">
      <span className="label">Fotos</span>
      {images.length > 0 && (
        <ul className="photo-grid">
          {images.map((img, i) => (
            <li
              key={img.id ?? img.url}
              className={dragging === i ? "dragging" : undefined}
              draggable={canEdit}
              onDragStart={(e) => {
                setDragging(i);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragOver={(e) => {
                if (dragging !== null) e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragging !== null) move(dragging, i);
                setDragging(null);
              }}
              onDragEnd={() => setDragging(null)}
            >
              <img src={thumbUrl(img)} alt={img.alt ?? ""} loading="lazy" draggable={false} />
              {i === 0 && <span className="photo-main">Principal</span>}
              {canEdit && (
                <div className="photo-actions">
                  <button type="button" aria-label="Mover antes" disabled={i === 0} onClick={() => move(i, i - 1)}>
                    ‹
                  </button>
                  <button
                    type="button"
                    aria-label="Mover después"
                    disabled={i === images.length - 1}
                    onClick={() => move(i, i + 1)}
                  >
                    ›
                  </button>
                  <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))}>
                    Quitar
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <>
          <div className="photo-upload">
            <label className={`btn btn-sm${!productId || room <= 0 || busy ? " disabled" : ""}`}>
              {busy ? "Subiendo…" : "Subir fotos"}
              <input
                type="file"
                className="sr-only"
                accept="image/jpeg,image/png,image/webp"
                multiple
                disabled={!productId || room <= 0 || busy}
                onChange={(e) => {
                  onFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </label>
            <span className="faint">
              {!productId
                ? "Guarda primero para subir fotos."
                : room > 0
                  ? `Hasta ${UPLOADS_MAX} fotos; la primera es la principal. Arrástralas para ordenarlas.`
                  : `Ya tiene ${UPLOADS_MAX} fotos subidas. Quita una para subir otra.`}
            </span>
          </div>
          <details className="disclosure">
            <summary>Agregar una foto por enlace</summary>
            <div className="disclosure-body spec-row">
              <input
                className="input"
                type="url"
                aria-label="Dirección de la foto"
                placeholder="https://tienda.com/foto.jpg"
                value={link}
                onChange={(e) => setLink(e.target.value)}
              />
              <button
                className="btn btn-sm"
                type="button"
                disabled={!/^https:\/\//.test(link)}
                onClick={() => {
                  onChange([...images, { url: link.trim() }]);
                  setLink("");
                }}
              >
                Agregar
              </button>
            </div>
          </details>
        </>
      )}
      {!canEdit && images.length === 0 && <span className="faint">Sin fotos.</span>}
    </div>
  );
}
